import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Loader2, Music, Send, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * AmbienceCheckSheet — bottom-sheet modal shown after a guest taps "Add"
 * on a search result. Calls the ambience check, then either offers a
 * "Send Request" CTA (allowed) or a "Try Another Song" CTA (rejected).
 *
 * Props:
 *   open            (bool)   — controls visibility.
 *   status          (string) — 'checking' | 'allowed' | 'rejected' | 'error'
 *   result          (obj)    — response from /check-ambience (or null while checking).
 *   track           (obj)    — the candidate track (for the accept-state card).
 *   venueName       (str)    — "The Beer Café, Koramangala" style label.
 *   onSend          (fn)     — invoked on "Send Request" (allowed state).
 *   onTryAnother    (fn)     — invoked on "Try Another Song" (rejected state).
 *   onClose         (fn)     — invoked on backdrop tap / grab-handle drag-down.
 *   sending         (bool)   — disables the Send CTA while the submit is in flight.
 */
export const AmbienceCheckSheet = ({
  open,
  status,
  result,
  track,
  venueName,
  onSend,
  onTryAnother,
  onClose,
  sending,
}) => {
  // Lock body scroll while the sheet is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const reasons = result?.mismatch_reasons || [];
  const rejectMessage = result?.reject_message || '';
  const matched = result?.matched || {};
  const matchTags = [
    ...(matched.moods || []),
    ...(matched.genres || []).slice(0, 1),
  ].slice(0, 3);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        data-testid="ambience-check-sheet"
      >
        {/* Backdrop */}
        <motion.button
          type="button"
          className="absolute inset-0 bg-black/70 backdrop-blur-md"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-label="Close"
          data-testid="ambience-check-backdrop"
        />

        {/* Sheet */}
        <motion.div
          className="relative w-full max-w-md rounded-t-[28px] border-t border-white/10 shadow-[0_-20px_60px_-15px_rgba(157,0,255,0.35)] overflow-hidden"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,10,25,0.98) 0%, rgba(6,4,12,0.98) 100%)',
            backdropFilter: 'blur(28px)',
          }}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        >
          {/* Grab handle */}
          <div className="pt-3 pb-2 flex justify-center">
            <div className="w-10 h-1 rounded-full bg-white/25" />
          </div>

          {/* Body */}
          <div className="px-6 pb-8 pt-2">
            {status === 'checking' && <CheckingState />}
            {status === 'allowed' && (
              <AllowedState
                track={track}
                venueName={venueName}
                matchTags={matchTags}
                onSend={onSend}
                sending={sending}
              />
            )}
            {status === 'rejected' && (
              <RejectedState
                reasons={reasons}
                rejectMessage={rejectMessage}
                onTryAnother={onTryAnother}
              />
            )}
            {status === 'error' && (
              <ErrorState
                onSend={onSend}
                onCancel={onClose}
                sending={sending}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/* --------------------------------------------------------------------- */
/* States                                                                */
/* --------------------------------------------------------------------- */

const CheckingState = () => (
  <div className="flex flex-col items-center py-8" data-testid="ambience-checking">
    {/* Pulsing rings — Coldplay style ambient warm-up */}
    <div className="relative w-24 h-24 mb-6">
      <motion.div
        className="absolute inset-0 rounded-full border-2 border-[#9D00FF]/50"
        animate={{ scale: [1, 1.4, 1], opacity: [0.7, 0, 0.7] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      />
      <motion.div
        className="absolute inset-2 rounded-full border-2 border-[#00F5FF]/60"
        animate={{ scale: [1, 1.3, 1], opacity: [0.9, 0.1, 0.9] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: 0.3 }}
      />
      <div className="absolute inset-6 rounded-full bg-gradient-to-br from-[#9D00FF] to-[#00F5FF] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
      </div>
    </div>
    <p className="text-xs uppercase tracking-[0.3em] text-[#00F5FF]/80 font-['JetBrains_Mono'] mb-2">
      Vibe Check · Reading the room
    </p>
    <p className="text-white text-lg font-['Outfit'] font-semibold text-center">
      Checking the vibe…
    </p>
  </div>
);


const AllowedState = ({ track, venueName, matchTags, onSend, sending }) => {
  const artists = Array.isArray(track?.artists)
    ? track.artists.join(', ')
    : (track?.artists || track?.artist_name || '');

  return (
    <div className="flex flex-col items-center" data-testid="ambience-allowed">
      {/* Neon teal glow ring around ✓ */}
      <div className="relative w-24 h-24 mb-4">
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            boxShadow: '0 0 32px 4px rgba(0, 245, 255, 0.6), inset 0 0 16px rgba(0, 245, 255, 0.4)',
            border: '2px solid rgba(0, 245, 255, 0.55)',
          }}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <div
          className="absolute inset-3 rounded-full flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle at 30% 30%, #22c55e, #16a34a 60%, #0f7a3a)',
            boxShadow: '0 0 24px rgba(34, 197, 94, 0.5)',
          }}
        >
          <Check className="w-10 h-10 text-white" strokeWidth={3} />
        </div>
      </div>

      <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F5FF]/80 font-['JetBrains_Mono'] mb-2">
        Vibe Check
      </p>
      <h2
        className="text-2xl font-['Outfit'] font-bold text-white text-center mb-1.5"
        style={{ textShadow: '0 0 20px rgba(0, 245, 255, 0.3)' }}
      >
        You&apos;re on the vibe
      </h2>
      <p className="text-gray-400 text-sm text-center mb-5 max-w-xs">
        This one blends right in with what&apos;s playing.
      </p>

      {/* Match tags */}
      {matchTags.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center mb-5" data-testid="ambience-match-tags">
          {matchTags.map((tag, i) => (
            <span
              key={`m-${i}`}
              className="text-xs font-semibold px-3 py-1 rounded-full lowercase"
              style={{
                background: 'rgba(0, 245, 255, 0.08)',
                border: '1px solid rgba(0, 245, 255, 0.35)',
                color: '#7ff1ff',
                boxShadow: '0 0 12px rgba(0, 245, 255, 0.15)',
              }}
              data-testid={`match-tag-${tag}`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Venue pill */}
      {venueName && (
        <div
          className="w-full rounded-2xl px-4 py-3 mb-3 flex items-center gap-3"
          style={{
            background: 'rgba(157, 0, 255, 0.06)',
            border: '1px solid rgba(157, 0, 255, 0.25)',
          }}
          data-testid="ambience-venue-pill"
        >
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#9D00FF] to-[#FF007F] flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 21s-8-6-8-12a8 8 0 1 1 16 0c0 6-8 12-8 12z" />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
          </div>
          <p className="text-white text-sm font-medium truncate flex-1">{venueName}</p>
        </div>
      )}

      {/* Song card */}
      <div
        className="w-full rounded-2xl px-3 py-3 mb-6 flex items-center gap-3"
        style={{
          background: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
        data-testid="ambience-song-card"
      >
        {track?.album_image ? (
          <img
            src={track.album_image}
            alt=""
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
            <Music className="w-5 h-5 text-gray-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">{track?.name}</p>
          <p className="text-gray-400 text-xs truncate">{artists}</p>
        </div>
      </div>

      <Button
        onClick={onSend}
        disabled={sending}
        className="w-full h-12 rounded-full font-semibold text-white text-sm shadow-lg"
        style={{
          background: 'linear-gradient(90deg, #9D00FF 0%, #FF007F 100%)',
          boxShadow: '0 8px 32px -8px rgba(157, 0, 255, 0.6)',
        }}
        data-testid="ambience-send-btn"
      >
        {sending ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
        ) : (
          <>Send Request Now <Send className="w-4 h-4 ml-2" /></>
        )}
      </Button>
    </div>
  );
};


const RejectedState = ({ reasons, rejectMessage, onTryAnother }) => (
  <div className="flex flex-col items-center" data-testid="ambience-rejected">
    {/* Neon red glow ring around ✕ */}
    <div className="relative w-24 h-24 mb-4">
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow: '0 0 32px 4px rgba(255, 0, 85, 0.6), inset 0 0 16px rgba(255, 0, 85, 0.4)',
          border: '2px solid rgba(255, 0, 85, 0.55)',
        }}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <div
        className="absolute inset-3 rounded-full flex items-center justify-center"
        style={{
          background: 'radial-gradient(circle at 30% 30%, #ef4444, #dc2626 60%, #991b1b)',
          boxShadow: '0 0 24px rgba(239, 68, 68, 0.5)',
        }}
      >
        <X className="w-10 h-10 text-white" strokeWidth={3} />
      </div>
    </div>

    <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF0055]/80 font-['JetBrains_Mono'] mb-2">
      Vibe Check
    </p>
    <h2
      className="text-2xl font-['Outfit'] font-bold text-white text-center mb-1.5"
      style={{ textShadow: '0 0 20px rgba(255, 0, 85, 0.3)' }}
    >
      Doesn&apos;t match the vibe
    </h2>
    <p className="text-gray-400 text-sm text-center mb-5 max-w-xs" data-testid="ambience-reject-message">
      {rejectMessage || "This one doesn't blend with what's playing tonight."}
    </p>

    {reasons.length > 0 && (
      <div className="flex flex-wrap gap-2 justify-center mb-6" data-testid="ambience-reject-tags">
        {reasons.map((tag, i) => (
          <span
            key={`r-${i}`}
            className="text-xs font-semibold px-3 py-1 rounded-full lowercase"
            style={{
              background: 'rgba(255, 0, 85, 0.08)',
              border: '1px solid rgba(255, 0, 85, 0.35)',
              color: '#ff7a9b',
              boxShadow: '0 0 12px rgba(255, 0, 85, 0.15)',
            }}
            data-testid={`reject-tag-${tag}`}
          >
            {tag}
          </span>
        ))}
      </div>
    )}

    <Button
      onClick={onTryAnother}
      className="w-full h-12 rounded-full font-semibold text-white text-sm"
      style={{
        background: 'linear-gradient(90deg, #FF0055 0%, #FF007F 100%)',
        boxShadow: '0 8px 32px -8px rgba(255, 0, 85, 0.5)',
      }}
      data-testid="ambience-try-another-btn"
    >
      <RefreshCw className="w-4 h-4 mr-2" /> Try Another Song
    </Button>
  </div>
);


const ErrorState = ({ onSend, onCancel, sending }) => (
  <div className="flex flex-col items-center" data-testid="ambience-error">
    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
      <Music className="w-7 h-7 text-gray-400" />
    </div>
    <h2 className="text-xl font-['Outfit'] font-bold text-white text-center mb-1.5">
      Ambience check unavailable
    </h2>
    <p className="text-gray-400 text-sm text-center mb-6 max-w-xs">
      We couldn&apos;t check the vibe. You can still send the request — the DJ will review.
    </p>
    <Button
      onClick={onSend}
      disabled={sending}
      className="w-full h-12 rounded-full font-semibold text-white text-sm mb-2"
      style={{ background: 'linear-gradient(90deg, #9D00FF 0%, #FF007F 100%)' }}
      data-testid="ambience-error-send-btn"
    >
      {sending ? (
        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
      ) : (
        <>Send Anyway <Send className="w-4 h-4 ml-2" /></>
      )}
    </Button>
    <button
      onClick={onCancel}
      className="text-gray-500 text-xs hover:text-white transition-colors py-2"
      data-testid="ambience-error-cancel-btn"
    >
      Cancel
    </button>
  </div>
);


export default AmbienceCheckSheet;
